import { Controller } from "@hotwired/stimulus";

// Таблица «Навыки программы» (/programs/:id/skills). Макет: ui/actual/ababy-skills-table.html.
//
// Строки рендерит сервер (program_skills/_skill_row), здесь их только фильтруем,
// сортируем, группируем и переставляем. Сортировка, поиск, фильтры и группировка
// живут в поповерах заголовков столбцов; активные фильтры — чипами над таблицей.
// Выбор строк включает панель групповых действий (форма ProgramSkillsBulkController).
const COLUMNS = {
  cat: { sort: true, filter: "cat", group: true },
  name: { sort: true, search: true },
  status: { sort: true, filter: "status", group: true },
  progress: { sort: true },
  reactions: { sort: true },
  method: { sort: true, filter: "method", group: true },
  introduced: { sort: true },
  gap: { sort: true },
};
const NUMERIC = ["progress", "reactions", "gap"];

export default class extends Controller {
  static targets = [
    "row",
    "body",
    "table",
    "scroller",
    "groupTemplate",
    "nothingTemplate",
    "filterBar",
    "chips",
    "count",
    "selectAll",
    "bulk",
    "bulkCount",
    "bulkIds",
    "bulkAction",
  ];
  static values = { i18n: Object, statuses: Array, reportUrl: String };

  connect() {
    this.rows = [...this.rowTargets]; // строки уходят из DOM при фильтрации — держим свои ссылки
    this.sortKey = null;
    this.sortDir = 1;
    this.group = "cat";
    this.filters = this.emptyFilters();
    this.selected = new Set();
    this.openColumnKey = null;

    this.onDocumentClick = (event) => {
      if (!event.target.closest(".sk-pop, .sk-hb, .sk-more, .sk-bulk-act"))
        this.closePops();
    };
    this.onKeydown = (event) => {
      if (event.key === "Escape") this.closePops();
    };
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeydown);

    this.render();
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
    this.closePops();
  }

  // ——— Рендер ———

  render() {
    const visible = this.sorted(this.rows.filter((row) => this.matches(row)));
    const nodes = [];

    if (this.group) {
      this.grouped(visible).forEach(([label, rows]) => {
        nodes.push(this.groupRow(label, rows));
        rows.forEach((row, i) => {
          row.classList.toggle("sk-gfirst", i === 0);
          nodes.push(row);
        });
      });
    } else {
      visible.forEach((row) => {
        row.classList.remove("sk-gfirst");
        nodes.push(row);
      });
    }
    if (!visible.length)
      nodes.push(
        this.nothingTemplateTarget.content.firstElementChild.cloneNode(true),
      );

    this.bodyTarget.replaceChildren(...nodes);
    this.tableTarget.classList.toggle("is-grouped-cat", this.group === "cat");
    this.renderHeader();
    this.renderFilterBar(visible.length);
    this.renderSelection(visible);
  }

  renderHeader() {
    this.tableTarget.querySelectorAll("th[data-col]").forEach((th) => {
      const key = th.dataset.col;
      const sorted = this.sortKey === key;
      th.classList.toggle("is-sorted", sorted);
      const arrow = th.querySelector(".sk-ar");
      arrow.hidden = !sorted;
      arrow.textContent = this.sortDir > 0 ? "▲" : "▼";
      th.querySelector(".sk-fd").hidden = !this.columnFiltered(key);
    });
  }

  renderFilterBar(shown) {
    const chips = [];
    if (this.filters.q)
      chips.push(["q", null, this.t("chipSearch", { q: this.filters.q })]);
    this.filters.status.forEach((v) =>
      chips.push([
        "status",
        v,
        this.t("chipStatus", { v: this.statusLabel(v) }),
      ]),
    );
    this.filters.method.forEach((v) =>
      chips.push([
        "method",
        v,
        this.t("chipMethod", { v: v || this.i18nValue.noMethod }),
      ]),
    );
    this.filters.cat.forEach((v) => chips.push(["cat", v, v]));

    this.filterBarTarget.hidden = chips.length === 0;
    this.chipsTarget.replaceChildren(
      ...chips.map(([key, value, label]) => {
        const chip = document.createElement("span");
        chip.className = "sk-fchip";
        chip.textContent = label;
        const remove = document.createElement("button");
        remove.type = "button";
        remove.setAttribute("aria-label", this.i18nValue.remove);
        remove.innerHTML = '<i class="fa-solid fa-xmark"></i>';
        remove.addEventListener("click", () => {
          if (key === "q") this.filters.q = "";
          else this.filters[key].delete(value);
          this.render();
        });
        chip.appendChild(remove);
        return chip;
      }),
    );
    this.countTarget.textContent = this.t("shown", {
      shown,
      total: this.rows.length,
    });
  }

  renderSelection(visible) {
    this.rows.forEach((row) => {
      const on = this.selected.has(row.dataset.id);
      row.classList.toggle("is-selected", on);
      const box = row.querySelector(".sk-cbx");
      if (box) box.setAttribute("aria-checked", on);
    });
    if (this.hasSelectAllTarget) {
      const all =
        visible.length > 0 &&
        visible.every((row) => this.selected.has(row.dataset.id));
      this.selectAllTarget.setAttribute("aria-checked", all);
    }
    if (this.hasBulkTarget) {
      this.bulkTarget.hidden = this.selected.size === 0;
      this.bulkCountTarget.textContent = this.t("selected", {
        count: this.selected.size,
      });
    }
  }

  groupRow(label, rows) {
    const tr =
      this.groupTemplateTarget.content.firstElementChild.cloneNode(true);
    const done = rows.filter((row) => row.dataset.complete === "true").length;
    tr.querySelector(".sk-gt").textContent = label;
    tr.querySelector(".sk-gc").textContent = this.t("groupCount", {
      done,
      total: rows.length,
    });
    return tr;
  }

  // ——— Фильтр, сортировка, группы ———

  matches(row) {
    const f = this.filters;
    if (f.q && !row.dataset.name.toLowerCase().includes(f.q.toLowerCase()))
      return false;
    if (f.status.size && !f.status.has(row.dataset.status)) return false;
    if (f.method.size && !f.method.has(row.dataset.method)) return false;
    if (f.cat.size && !f.cat.has(row.dataset.cat)) return false;
    return true;
  }

  sorted(rows) {
    const key = this.sortKey;
    const position = (row) => Number(row.dataset.position);
    return rows.slice().sort((a, b) => {
      if (key) {
        const cmp = this.compare(
          this.sortValue(a, key),
          this.sortValue(b, key),
        );
        if (cmp !== 0) return cmp * this.sortDir;
      }
      return position(a) - position(b);
    });
  }

  sortValue(row, key) {
    const d = row.dataset;
    if (NUMERIC.includes(key)) return Number(d[key]);
    if (key === "status") return this.statusesValue.indexOf(d.status);
    return d[key] || "";
  }

  compare(a, b) {
    if (typeof a === "number") return a - b;
    if (a === b) return 0;
    if (!a) return 1; // пустые — в конец
    if (!b) return -1;
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  }

  grouped(rows) {
    const key = this.groupField();
    const buckets = new Map();
    rows.forEach((row) => {
      const value = row.dataset[key] || "";
      if (!buckets.has(value)) buckets.set(value, []);
      buckets.get(value).push(row);
    });
    return this.groupOrder([...buckets.keys()]).map((value) => [
      this.groupLabel(value),
      buckets.get(value),
    ]);
  }

  groupOrder(values) {
    if (this.group === "status")
      return values.sort(
        (a, b) => this.statusesValue.indexOf(a) - this.statusesValue.indexOf(b),
      );
    if (this.group === "method")
      return values.sort((a, b) => this.compare(a, b));
    // категории — в порядке, в котором они впервые встречаются в программе
    const order = [...new Set(this.rows.map((row) => row.dataset.cat))];
    return values.sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }

  groupField() {
    return { cat: "cat", status: "status", method: "method" }[this.group];
  }

  groupLabel(value) {
    if (this.group === "status") return this.statusLabel(value);
    if (this.group === "method") return value || this.i18nValue.noMethod;
    return value;
  }

  columnFiltered(key) {
    const col = COLUMNS[key];
    if (col.search) return !!this.filters.q;
    return col.filter ? this.filters[col.filter].size > 0 : false;
  }

  // ——— Поповер заголовка ———

  openColumn(event) {
    event.stopPropagation();
    const key = event.currentTarget.dataset.col;
    const wasOpen = this.openColumnKey === key;
    this.closePops();
    if (wasOpen) return;

    const th = event.currentTarget.closest("th");
    th.appendChild(this.columnPopover(key));
    this.openColumnKey = key;
    this.scrollerTarget.classList.add("is-popped");
    th.querySelector(".sk-pop input")?.focus();
  }

  columnPopover(key) {
    const col = COLUMNS[key];
    const pop = document.createElement("div");
    pop.className = "sk-pop";
    const add = (el) => {
      pop.appendChild(el);
      return el;
    };
    const sep = () => add(this.el("div", "sk-sep"));

    if (col.sort) {
      add(
        this.item(`↑ ${this.i18nValue.sortAsc}`, () => this.applySort(key, 1)),
      );
      add(
        this.item(`↓ ${this.i18nValue.sortDesc}`, () =>
          this.applySort(key, -1),
        ),
      );
    }
    if (col.search) {
      sep();
      add(this.el("div", "sk-lbl", this.i18nValue.search));
      const input = add(this.el("input", "field-input"));
      input.type = "search";
      input.placeholder = this.i18nValue.searchPlaceholder;
      input.value = this.filters.q;
      input.addEventListener("input", () => {
        this.filters.q = input.value.trim();
        this.render();
      });
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          this.closePops();
        }
      });
    }
    if (col.filter) {
      sep();
      add(this.el("div", "sk-lbl", this.i18nValue.show));
      this.filterValues(col.filter).forEach(([value, label, count]) => {
        const option = add(this.el("button", "sk-opt"));
        option.type = "button";
        const box = this.el("span", "sk-cbx");
        box.setAttribute("aria-checked", this.filters[col.filter].has(value));
        box.innerHTML = '<i class="fa-solid fa-check"></i>';
        option.append(
          box,
          document.createTextNode(label),
          this.el("span", "sk-n", String(count)),
        );
        option.addEventListener("click", () => {
          const set = this.filters[col.filter];
          set.has(value) ? set.delete(value) : set.add(value);
          box.setAttribute("aria-checked", set.has(value));
          this.render();
        });
      });
    }
    if (col.group) {
      sep();
      add(
        this.item(
          `${this.group === key ? "✓ " : ""}${this.i18nValue.groupBy}`,
          () => this.applyGroup(key),
        ),
      );
      if (this.group)
        add(this.item(this.i18nValue.noGroup, () => this.applyGroup("")));
    }
    return pop;
  }

  filterValues(field) {
    const counts = new Map();
    this.rows.forEach((row) => {
      const value = row.dataset[field] || "";
      counts.set(value, (counts.get(value) || 0) + 1);
    });
    const values =
      field === "status"
        ? this.statusesValue.filter((s) => counts.has(s))
        : [...counts.keys()].sort((a, b) => this.compare(a, b));
    return values.map((value) => {
      const label =
        field === "status"
          ? this.statusLabel(value)
          : value || this.i18nValue.noMethod;
      return [value, label, counts.get(value)];
    });
  }

  applySort(key, dir) {
    this.sortKey = key;
    this.sortDir = dir;
    this.closePops();
    this.render();
  }

  applyGroup(key) {
    this.group = key;
    this.closePops();
    this.render();
  }

  resetFilters() {
    this.filters = this.emptyFilters();
    this.render();
  }

  // ——— Выбор и групповые действия ———

  toggleRow(event) {
    const id = event.currentTarget.closest("tr").dataset.id;
    this.selected.has(id) ? this.selected.delete(id) : this.selected.add(id);
    this.renderSelection(this.visibleRows());
  }

  toggleAll() {
    const visible = this.visibleRows();
    const all = visible.every((row) => this.selected.has(row.dataset.id));
    visible.forEach((row) =>
      all
        ? this.selected.delete(row.dataset.id)
        : this.selected.add(row.dataset.id),
    );
    this.renderSelection(visible);
  }

  clearSelection() {
    this.selected.clear();
    this.closePops();
    this.renderSelection(this.visibleRows());
  }

  toggleBulkPop(event) {
    event.stopPropagation();
    const pop = this.bulkTarget.querySelector(
      `[data-bulk-pop="${event.currentTarget.dataset.pop}"]`,
    );
    const willOpen = pop.hidden;
    this.closePops();
    pop.hidden = !willOpen;
    if (willOpen) pop.querySelector("textarea")?.focus();
  }

  // Перед отправкой формы: какое действие и какие навыки.
  fillBulkIds(event) {
    const action = event.submitter?.dataset.bulkAction;
    if (!action) {
      event.preventDefault();
      return;
    }

    if (action === "discontinue") {
      const reason = this.bulkTarget.querySelector("textarea[name='reason']");
      if (!reason.value.trim()) {
        event.preventDefault();
        reason.focus();
        return;
      }
    }
    this.bulkActionTarget.value = action;
    this.bulkIdsTarget.replaceChildren(
      ...[...this.selected].map((id) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "ids[]";
        input.value = id;
        return input;
      }),
    );
  }

  toReport() {
    const url = new URL(this.reportUrlValue, window.location.origin);
    this.selected.forEach((id) =>
      url.searchParams.append("program_skill_ids[]", id),
    );
    window.location.assign(url.toString());
  }

  // ——— Меню строки ———

  toggleMenu(event) {
    event.stopPropagation();
    const button = event.currentTarget;
    const menu = button.nextElementSibling;
    const willOpen = menu.hidden;
    this.closePops();
    if (!willOpen) return;

    menu.hidden = false;
    button.setAttribute("aria-expanded", "true");
    this.scrollerTarget.classList.add("is-popped");
  }

  // ——— Служебное ———

  closePops() {
    this.element.querySelectorAll("th .sk-pop").forEach((pop) => pop.remove());
    this.element
      .querySelectorAll(".sk-x .sk-pop, [data-bulk-pop]")
      .forEach((pop) => {
        pop.hidden = true;
      });
    this.element
      .querySelectorAll(".sk-more[aria-expanded='true']")
      .forEach((b) => b.setAttribute("aria-expanded", "false"));
    if (this.hasScrollerTarget)
      this.scrollerTarget.classList.remove("is-popped");
    this.openColumnKey = null;
  }

  visibleRows() {
    return this.rows.filter((row) => row.isConnected);
  }

  emptyFilters() {
    return { q: "", status: new Set(), method: new Set(), cat: new Set() };
  }

  statusLabel(status) {
    return this.i18nValue.statuses?.[status] || status;
  }

  item(label, onClick) {
    const button = this.el("button", "sk-pi", label);
    button.type = "button";
    button.addEventListener("click", onClick);
    return button;
  }

  el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  t(key, vars = {}) {
    return Object.entries(vars).reduce(
      (str, [name, value]) => str.replaceAll(`%{${name}}`, value),
      this.i18nValue[key] || key,
    );
  }
}
